import { Camera } from "./Camera"
import type { IRenderer } from "./IRenderer"
import { Interaction } from "./interaction"
import { getLOD } from "./lod"
import { QuadTree } from "./QuadTree"
import { Scene } from "./Scene"
import {
  type CanvasEdge,
  type CanvasNode,
  DEFAULT_CONFIG,
  type DebugStats,
  type EdgeBatch,
  type GraphCanvasCallbacks,
  type GraphCanvasConfig,
  LODLevel,
  type ViewportInsets,
  type ViewportRect,
} from "./types"
import { WebGLRenderer } from "./WebGLRenderer"
import { onWasmReady } from "./wasmRuntime"

const FOCUS_MIN_SCALE = 0.8

export class GraphCanvas {
  readonly camera = new Camera()
  private quadtree = new QuadTree()
  private renderer: IRenderer
  private interaction: Interaction | null = null
  private scene = new Scene()

  private edgeCount = 0
  private edgeBatchTemplate: EdgeBatch[] = []

  private selectedId: string | null = null
  private hoveredId: string | null = null
  private highlightedIds: Set<string> | null = null
  private attentionIds: Set<string> | null = null
  private visibleEdgeKinds: Set<string> | null = null
  private config: GraphCanvasConfig = DEFAULT_CONFIG

  private dirty = true
  private needsFitView = false
  private rafId = 0
  private width = 0
  private height = 0
  private callbacks: GraphCanvasCallbacks = {}
  private resizeObserver: ResizeObserver | null = null
  private unsubscribeLabelReady: (() => void) | null = null
  private interactingUntil = 0
  private wasInteracting = false
  private lastFrameTime = 0
  private reducedMotion = false
  private motionQuery: MediaQueryList | null = null
  private viewportInsets: Required<ViewportInsets> = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  }

  // FPS counters
  fps = 0 // rAF ticks per second (baseline should match display refresh)
  renderFps = 0 // only frames where actual rendering happened
  rafMs = 0 // average requestAnimationFrame interval over the last FPS window
  renderMs = 0 // ms for last render() call
  renderAvgMs = 0 // average render() cost over the last FPS window
  cacheHit = false // true when scene cache was reused (no re-render)
  private fpsFrames = 0
  private renderFrames = 0
  private renderMsSum = 0
  private fpsLastTime = performance.now()
  private fpsCallback: (() => void) | null = null

  constructor(renderer?: IRenderer) {
    this.renderer = renderer ?? new WebGLRenderer()
  }

  private onMotionPreferenceChange = (event: MediaQueryListEvent) => {
    this.reducedMotion = event.matches
    if (event.matches) this.camera.finishAnimation()
    this.dirty = true
  }

  mount(container: HTMLElement, callbacks: GraphCanvasCallbacks = {}) {
    this.callbacks = callbacks
    this.renderer.init(container)
    if (typeof matchMedia === "function") {
      this.motionQuery = matchMedia("(prefers-reduced-motion: reduce)")
      this.reducedMotion = this.motionQuery.matches
      this.motionQuery.addEventListener("change", this.onMotionPreferenceChange)
    }

    const el = this.renderer.getElement()
    el.style.cursor = "grab"

    this.interaction = new Interaction(
      el,
      this.camera,
      this.quadtree,
      this.scene,
      {
        onNodeClick: (id) => {
          this.selectedId = id
          this.dirty = true
          callbacks.onNodeClick?.(id)
        },
        onNodeHover: (id) => {
          if (id !== this.hoveredId) {
            this.hoveredId = id
            this.dirty = true
          }
          callbacks.onNodeHover?.(id)
        },
        onPaneClick: () => {
          this.selectedId = null
          this.dirty = true
          callbacks.onPaneClick?.()
        },
      },
      (interacting = false) => {
        if (interacting) this.interactingUntil = performance.now() + 120
        this.dirty = true
      },
    )

    this.resizeObserver = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      if (width === this.width && height === this.height) return
      this.width = width
      this.height = height
      this.renderer.resize(width, height)
      this.dirty = true
    })
    this.resizeObserver.observe(container)

    this.unsubscribeLabelReady = onWasmReady(() => {
      this.renderer.invalidateSceneCache()
      this.dirty = true
    })

    this.startLoop()
  }

  unmount() {
    this.stopLoop()
    this.camera.stop()
    this.interaction?.destroy()
    this.interaction = null
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.unsubscribeLabelReady?.()
    this.unsubscribeLabelReady = null
    this.motionQuery?.removeEventListener(
      "change",
      this.onMotionPreferenceChange,
    )
    this.motionQuery = null
    this.scene.free()
    this.renderer.destroy()
  }

  // ── Data API ─────────────────────────────────────────

  setData(nodes: CanvasNode[], edges: CanvasEdge[]) {
    this.scene.setData(nodes)
    this.edgeCount = edges.length

    // Pre-batch edges by color+dash with endpoints resolved once.
    const batchMap = new Map<string, EdgeBatch>()
    for (const edge of edges) {
      const from = this.scene.indexOf(edge.fromId)
      const to = this.scene.indexOf(edge.toId)
      if (from === undefined || to === undefined) continue

      const key = `${edge.edgeKind}|${edge.color}|${edge.dash?.join(",") ?? ""}`
      let batch = batchMap.get(key)
      if (!batch) {
        batch = {
          edgeKind: edge.edgeKind,
          color: edge.color,
          dash: edge.dash ?? [],
          edges: [],
        }
        batchMap.set(key, batch)
      }
      batch.edges.push({ from, to })
    }
    this.edgeBatchTemplate = [...batchMap.values()]

    this.quadtree.rebuild(this.scene)
    this.renderer.setScene?.(this.scene)
    this.needsFitView = true
    this.renderer.invalidateSceneCache()
    this.dirty = true
  }

  setSelectedNode(id: string | null) {
    this.selectedId = id
    this.dirty = true
  }

  setHoveredNode(id: string | null) {
    this.hoveredId = id
    this.dirty = true
  }

  setHighlight(ids: Set<string> | null) {
    this.highlightedIds = ids
    this.dirty = true
  }

  setAttention(ids: Set<string> | null) {
    this.attentionIds = ids
    this.dirty = true
  }

  setVisibleEdgeKinds(kinds: Set<string> | null) {
    this.visibleEdgeKinds = kinds
    this.renderer.invalidateSceneCache()
    this.dirty = true
  }

  setConfig(config: Partial<GraphCanvasConfig>) {
    const edgeConfigChanged =
      (config.layoutDirection !== undefined &&
        config.layoutDirection !== this.config.layoutDirection) ||
      (config.edgeWidth !== undefined &&
        config.edgeWidth !== this.config.edgeWidth)

    this.config = { ...this.config, ...config }
    if (edgeConfigChanged) this.renderer.invalidateSceneCache()
    this.dirty = true
  }

  // ── Viewport ─────────────────────────────────────────

  getWidth() {
    return this.width
  }
  getHeight() {
    return this.height
  }

  getScene() {
    return this.scene
  }

  getCameraState() {
    return this.camera.getState()
  }

  getVisibleRect() {
    return this.camera.getVisibleRect(this.width, this.height)
  }

  screenToWorld(x: number, y: number) {
    return this.camera.screenToWorld(x, y)
  }

  worldToScreen(x: number, y: number) {
    return this.camera.worldToScreen(x, y)
  }

  getNodes(): CanvasNode[] {
    const nodes: CanvasNode[] = []
    for (let i = 0; i < this.scene.count; i++) {
      nodes.push({
        id: this.scene.ids[i],
        kind: "",
        name: this.scene.names[i],
        spec: {},
        x: this.scene.minX(i),
        y: this.scene.minY(i),
        width: this.scene.maxX(i) - this.scene.minX(i),
        height: this.scene.maxY(i) - this.scene.minY(i),
        color: this.scene.colors[i],
      })
    }
    return nodes
  }

  getVisibleNodes(): CanvasNode[] {
    const visible = this.camera.getVisibleRect(this.width, this.height)
    const indices = this.quadtree.queryRect(visible)
    return indices.map((i) => ({
      id: this.scene.ids[i],
      kind: "",
      name: this.scene.names[i],
      spec: {},
      x: this.scene.minX(i),
      y: this.scene.minY(i),
      width: this.scene.maxX(i) - this.scene.minX(i),
      height: this.scene.maxY(i) - this.scene.minY(i),
      color: this.scene.colors[i],
    }))
  }

  setViewportInsets(insets: ViewportInsets, animate = true) {
    const previous = this.safeViewport()
    const worldCenter = this.camera.screenToWorld(previous.cx, previous.cy)
    this.viewportInsets = {
      top: Math.max(0, insets.top ?? 0),
      right: Math.max(0, insets.right ?? 0),
      bottom: Math.max(0, insets.bottom ?? 0),
      left: Math.max(0, insets.left ?? 0),
    }
    const next = this.safeViewport()
    if (this.width > 0 && this.height > 0) {
      const x = next.cx - worldCenter.x * this.camera.scale
      const y = next.cy - worldCenter.y * this.camera.scale
      if (animate && !this.reducedMotion) {
        this.camera.moveTo(x, y, this.camera.scale)
      } else {
        this.camera.setImmediate(x, y, this.camera.scale)
      }
    }
    this.dirty = true
  }

  getDebugStats(): DebugStats {
    const lod = getLOD(this.camera.scale)
    const lodNames = { [LODLevel.Dot]: "Dot" }
    const visible = this.camera.getVisibleRect(this.width, this.height)
    const visibleIndices = this.quadtree.queryRect(visible)
    return {
      fps: this.fps,
      renderFps: this.renderFps,
      rafMs: this.rafMs,
      renderMs: this.renderMs,
      renderAvgMs: this.renderAvgMs,
      cacheHit: this.cacheHit,
      edgeCount: this.edgeCount,
      nodeCount: this.scene.count,
      visibleNodes: visibleIndices.length,
      lod: lodNames[lod] ?? "?",
      scale: this.camera.scale,
      animating: this.camera.animating,
      dirty: this.dirty,
      timings: this.renderer.timings,
    }
  }

  fitView(
    animate = true,
    signal?: AbortSignal,
  ): Promise<"settled" | "interrupted" | "unavailable"> {
    if (this.scene.count === 0 || this.width === 0 || this.height === 0) {
      return Promise.resolve("unavailable")
    }
    const bounds = this.computeBounds()
    const viewport = this.safeViewport()
    const operation = this.camera.fitView(
      bounds,
      viewport.width,
      viewport.height,
      0.1,
      animate && !this.reducedMotion,
      viewport.left,
      viewport.top,
    )
    this.dirty = true
    return operation === null
      ? Promise.resolve("unavailable")
      : this.camera.waitForOperation(operation, signal)
  }

  resetView(animate = true) {
    this.fitView(animate)
  }

  zoomIn(animate = true) {
    this.zoomBy(1.25, animate)
  }

  zoomOut(animate = true) {
    this.zoomBy(0.8, animate)
  }

  zoomBy(factor: number, animate = true) {
    const { cx, cy } = this.safeViewport()
    if (animate && !this.reducedMotion) {
      this.camera.zoomByAnimated(factor, cx, cy)
    } else {
      this.camera.zoomBy(factor, cx, cy)
    }
    this.needsFitView = false
    this.dirty = true
  }

  centerOn(worldX: number, worldY: number, animate = false) {
    const viewport = this.safeViewport()
    const x = viewport.cx - worldX * this.camera.scale
    const y = viewport.cy - worldY * this.camera.scale
    if (animate && !this.reducedMotion) {
      this.camera.moveTo(x, y, this.camera.scale)
    } else {
      this.camera.setImmediate(x, y, this.camera.scale)
    }
    this.needsFitView = false
    this.dirty = true
  }

  focusNode(id: string, animate = true): boolean {
    const i = this.scene.indexOf(id)
    if (i === undefined || this.width === 0 || this.height === 0) return false
    this.needsFitView = false
    const cx = (this.scene.minX(i) + this.scene.maxX(i)) / 2
    const cy = (this.scene.minY(i) + this.scene.maxY(i)) / 2
    const scale = Math.max(this.camera.scale, FOCUS_MIN_SCALE)
    const viewport = this.safeViewport()
    const x = viewport.cx - cx * scale
    const y = viewport.cy - cy * scale
    if (animate && !this.reducedMotion) {
      this.camera.moveTo(x, y, scale)
    } else {
      this.camera.setImmediate(x, y, scale)
    }
    this.dirty = true
    return true
  }

  private safeViewport() {
    const left = Math.min(this.viewportInsets.left, this.width)
    const top = Math.min(this.viewportInsets.top, this.height)
    const right = Math.min(
      this.viewportInsets.right,
      Math.max(0, this.width - left),
    )
    const bottom = Math.min(
      this.viewportInsets.bottom,
      Math.max(0, this.height - top),
    )
    const width = Math.max(1, this.width - left - right)
    const height = Math.max(1, this.height - top - bottom)
    return {
      left,
      top,
      width,
      height,
      cx: left + width / 2,
      cy: top + height / 2,
    }
  }

  private computeBounds(): ViewportRect {
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (let i = 0; i < this.scene.count; i++) {
      if (this.scene.minX(i) < minX) minX = this.scene.minX(i)
      if (this.scene.minY(i) < minY) minY = this.scene.minY(i)
      if (this.scene.maxX(i) > maxX) maxX = this.scene.maxX(i)
      if (this.scene.maxY(i) > maxY) maxY = this.scene.maxY(i)
    }
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
  }

  // ── Render loop ──────────────────────────────────────

  onFps(cb: () => void) {
    this.fpsCallback = cb
  }

  private startLoop() {
    this.lastFrameTime = 0
    const tick = (now: number) => {
      this.rafId = requestAnimationFrame(tick)

      // FPS tracking
      this.fpsFrames++
      if (now - this.fpsLastTime >= 1000) {
        const elapsed = now - this.fpsLastTime
        this.fps = Math.round((this.fpsFrames * 1000) / elapsed)
        this.renderFps = Math.round((this.renderFrames * 1000) / elapsed)
        this.rafMs = this.fpsFrames > 0 ? elapsed / this.fpsFrames : 0
        this.renderAvgMs =
          this.renderFrames > 0 ? this.renderMsSum / this.renderFrames : 0
        this.fpsFrames = 0
        this.renderFrames = 0
        this.renderMsSum = 0
        this.fpsLastTime = now
        this.fpsCallback?.()
      }

      // Deferred fitView: wait until canvas has actual dimensions
      if (
        this.needsFitView &&
        this.width > 0 &&
        this.height > 0 &&
        this.scene.count > 0
      ) {
        this.needsFitView = false
        const bounds = this.computeBounds()
        const viewport = this.safeViewport()
        this.camera.fitView(
          bounds,
          viewport.width,
          viewport.height,
          0.1,
          false,
          viewport.left,
          viewport.top,
        )
        this.dirty = true
      }

      const dt =
        this.lastFrameTime === 0 ? 1 / 60 : (now - this.lastFrameTime) / 1000
      this.lastFrameTime = now
      const wasAnimating = this.camera.animating
      const animating = this.camera.tick(dt)
      const interacting = now < this.interactingUntil
      if (wasAnimating) this.dirty = true
      if (this.renderer.effectsActive()) this.dirty = true
      if (this.wasInteracting && !interacting) {
        // One settled frame restores labels and lets scale changes rebuild cache.
        this.dirty = true
      }
      this.wasInteracting = interacting
      if (!this.dirty) return

      const lod = getLOD(this.camera.scale)
      const visible = this.camera.getVisibleRect(this.width, this.height)
      const visibleIndices = this.quadtree.queryRect(visible)

      // Filter semantic edge batches without replacing scene data or refitting.
      const edgeBatches = this.visibleEdgeKinds
        ? this.edgeBatchTemplate.filter((batch) =>
            this.visibleEdgeKinds!.has(batch.edgeKind),
          )
        : this.edgeBatchTemplate

      this.renderer.render({
        visibleIndices,
        edgeBatches,
        camera: this.camera.getState(),
        lod,
        config: this.config,
        selectedId: this.selectedId,
        hoveredId: this.hoveredId,
        highlightedIds: this.highlightedIds,
        attentionIds: this.attentionIds,
        timeSec: now / 1000,
        canvasWidth: this.width,
        canvasHeight: this.height,
        animating,
        interacting,
      })

      this.renderMs = this.renderer.lastRenderMs
      this.renderMsSum += this.renderMs
      this.cacheHit = this.renderer.lastCacheHit
      this.dirty = false
      this.renderFrames++
      this.callbacks.onRender?.()
    }

    this.rafId = requestAnimationFrame(tick)
  }

  private stopLoop() {
    cancelAnimationFrame(this.rafId)
    this.lastFrameTime = 0
  }
}
